import React, { useEffect, useState } from "react";
import {
  Loader2, Sparkles, DollarSign, Package, UserCheck, ListChecks, Send,
  RefreshCw, AlertTriangle, MapPin, Clock, FileText, Wand2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { getOpsAnalysis, refreshOpsAnalysis } from "@/lib/api";

const fmtMoney = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TINT = {
  navy: { color: "#0B2545", bg: "rgba(11,37,69,0.08)" },
  gold: { color: "#7A5E12", bg: "rgba(212,175,55,0.18)" },
  green: { color: "#0F6D5D", bg: "rgba(15,109,93,0.12)" },
  red: { color: "#9B1C1C", bg: "rgba(155,28,28,0.10)" },
  slate: { color: "#475569", bg: "#F1F5F9" },
};

export default function OpsAnalysisPanel({ leadId, onApplyStaff, onApplyPrice, onSessionExpired }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bundle, setBundle] = useState(null);

  const load = async () => {
    try {
      const b = await getOpsAnalysis(leadId);
      setBundle(b);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error(err?.response?.data?.detail || "Could not load ops analysis.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const handleRefresh = async (skipLlm = false) => {
    setRefreshing(true);
    try {
      const b = await refreshOpsAnalysis(leadId, { force_refresh: true, skip_llm: skipLlm });
      setBundle(b);
      toast.success("Operations analysis refreshed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed.");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border p-5 grid place-items-center" style={{ borderColor: "#E2E8F0" }}
           data-testid="ops-panel-loading">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} />
        <p className="text-xs mt-1.5" style={{ color: "#64748B" }}>Analyzing this lead…</p>
      </div>
    );
  }
  if (!bundle) return null;

  const { summary, pricing, supplies, staff, checklist, dispatch_assistant, llm_used } = bundle;

  return (
    <div className="space-y-4 rounded-xl border p-4"
         style={{ borderColor: "rgba(212,175,55,0.4)", background: "linear-gradient(180deg, rgba(212,175,55,0.04), rgba(11,37,69,0.02))" }}
         data-testid="ops-analysis-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-8 w-8 rounded-lg" style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
            <Sparkles className="h-4 w-4" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#7A5E12" }}>
              Operations AI {llm_used ? "" : "(rule-based)"}
            </div>
            <h3 className="font-display text-base font-semibold" style={{ color: "#0B2545" }}>
              Smart Lead Analysis
            </h3>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => handleRefresh(false)} disabled={refreshing}
                data-testid="ops-refresh">
          {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Re-analyze
        </Button>
      </div>

      {/* Summary narrative */}
      <div className="rounded-lg border bg-white p-3" style={{ borderColor: "#E2E8F0" }} data-testid="ops-summary">
        <p className="text-xs leading-relaxed" style={{ color: "#0B2545" }}>{summary}</p>
      </div>

      {/* Dispatch Assistant — featured if PAID+SCHEDULED */}
      {dispatch_assistant?.ready_to_dispatch && (
        <DispatchAssistantCard a={dispatch_assistant} onApplyStaff={onApplyStaff} />
      )}

      {/* Pricing */}
      <Section title="Pricing Recommendation" icon={DollarSign} tone="green" testid="ops-pricing">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border bg-white p-3" style={{ borderColor: "#E2E8F0" }}>
            <ul className="space-y-1">
              {pricing.breakdown.map((b, i) => (
                <li key={i} className="flex items-center justify-between text-xs" data-testid={`ops-price-row-${i}`}>
                  <span style={{ color: "#475569" }}>{b.label}</span>
                  <span className="font-semibold tabular-nums" style={{ color: b.amount < 0 ? "#9B1C1C" : "#0B2545" }}>
                    {b.amount >= 0 ? "+" : ""}{fmtMoney(b.amount)}
                  </span>
                </li>
              ))}
              <li className="flex items-center justify-between text-sm pt-2 mt-1 border-t" style={{ borderColor: "#E2E8F0" }}>
                <span className="font-display font-semibold" style={{ color: "#0B2545" }}>Recommended Total</span>
                <span className="font-display font-bold text-base tabular-nums" style={{ color: "#0F6D5D" }}
                      data-testid="ops-price-total">{fmtMoney(pricing.recommended_total)}</span>
              </li>
            </ul>
            <Button size="sm" variant="outline" className="mt-3 w-full"
                    onClick={() => onApplyPrice?.(pricing.recommended_total)}
                    data-testid="ops-apply-price">
              <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Apply ${pricing.recommended_total.toFixed(2)} to Amount Due
            </Button>
          </div>
          <div className="rounded-lg border bg-white p-3 space-y-1.5 text-xs" style={{ borderColor: "#E2E8F0" }}>
            <Row label="Urgency" value={pricing.urgency} tone={pricing.urgency === "Immediate" ? "red" : pricing.urgency === "Same Day" ? "gold" : "navy"} />
            <Row label="Surge multiplier" value={`×${pricing.urgency_multiplier.toFixed(2)}`} tone="navy" />
            <Row label="Travel" value={pricing.travel_miles_estimated != null ? `${pricing.travel_miles_estimated} mi · ${fmtMoney(pricing.travel_fee)}` : "Geocode address for travel calc"} tone="slate" />
            <Row label="Primary service" value={pricing.primary_service} tone="slate" />
          </div>
        </div>
      </Section>

      {/* Supplies + Forms */}
      <Section title="Supply Recommendation" icon={Package} tone="gold" testid="ops-supplies">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border bg-white p-3" style={{ borderColor: "#E2E8F0" }}>
            <div className="text-[10px] uppercase tracking-[0.16em] mb-1.5" style={{ color: "#64748B" }}>
              Supplies ({supplies.supplies.length})
              {supplies.tests_detected?.length > 0 && (
                <span className="ml-2 normal-case">· tests detected: <b>{supplies.tests_detected.join(", ")}</b></span>
              )}
            </div>
            <ul className="space-y-0.5 max-h-44 overflow-y-auto">
              {supplies.supplies.map((s, i) => (
                <li key={i} className="text-[11px]" style={{ color: "#0B2545" }} data-testid={`ops-supply-${i}`}>
                  • {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border bg-white p-3 space-y-2" style={{ borderColor: "#E2E8F0" }}>
            {supplies.forms_required?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] mb-1" style={{ color: "#64748B" }}>
                  <FileText className="h-3 w-3 inline mr-1 -mt-0.5" /> Forms required
                </div>
                <ul className="space-y-0.5">
                  {supplies.forms_required.map((f, i) => {
                    const missing = f.startsWith("MISSING:");
                    return (
                      <li key={i} className="text-[11px] flex items-start gap-1"
                          style={{ color: missing ? "#9B1C1C" : "#0B2545" }}
                          data-testid={`ops-form-${i}`}>
                        {missing && <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />}
                        <span>{f}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {supplies.transport_requirements?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] mb-1" style={{ color: "#64748B" }}>
                  Transport requirements
                </div>
                <ul className="space-y-0.5">
                  {supplies.transport_requirements.map((t, i) => (
                    <li key={i} className="text-[11px]" style={{ color: "#0B2545" }}>• {t}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* Staff Suggestions */}
      <Section title="Staff Recommendation" icon={UserCheck} tone="navy" testid="ops-staff">
        {staff.length === 0 ? (
          <p className="text-xs" style={{ color: "#64748B" }}>No staff in the directory yet. Upload an employee_staff document under <strong>Operations &amp; Docs</strong>.</p>
        ) : (
          <ul className="space-y-2">
            {staff.map((s) => (
              <li key={s.id}
                  className="rounded-lg border bg-white p-2.5 flex items-center justify-between gap-2"
                  style={{ borderColor: "#E2E8F0" }}
                  data-testid={`ops-staff-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-semibold truncate" style={{ color: "#0B2545" }}>{s.name}</span>
                    <Badge tone={s.availability === "available" ? "green" : s.availability === "busy" ? "red" : "gold"}>
                      {s.availability}
                    </Badge>
                    <Badge tone={s.expiry_status === "expired" ? "red" : s.expiry_status === "expiring_soon" ? "gold" : "green"}>
                      {s.expiry_status === "ok" ? "current" : s.expiry_status.replace(/_/g, " ")}
                    </Badge>
                    {s.distance_miles != null && (
                      <Badge tone="slate"><MapPin className="h-2.5 w-2.5 mr-0.5 inline" />{s.distance_miles} mi</Badge>
                    )}
                    {s.conflicts?.length > 0 && (
                      <Badge tone="red"><AlertTriangle className="h-2.5 w-2.5 mr-0.5 inline" />{s.conflicts.length} conflict</Badge>
                    )}
                  </div>
                  {s.notes?.length > 0 && (
                    <p className="text-[10px] mt-0.5" style={{ color: "#64748B" }}>{s.notes.join(" · ")}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded"
                        style={{ background: "rgba(11,37,69,0.06)", color: "#0B2545" }}>
                    score {s.score.toFixed(1)}
                  </span>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]"
                          onClick={() => onApplyStaff?.(s)}
                          data-testid={`ops-assign-${s.id}`}>
                    <Send className="h-3 w-3 mr-1" /> Assign
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Dispatch Preparation Checklist */}
      <Section title="Dispatch Preparation Checklist" icon={ListChecks} tone="green" testid="ops-checklist">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
          <Tile label="Travel" value={
            checklist.estimated_travel_miles != null
              ? `~${checklist.estimated_travel_miles} mi · ${checklist.estimated_travel_time}`
              : "Geocode the lead address to estimate travel"
          } icon={MapPin} />
          <Tile label="Top staff suggestion" value={checklist.primary_staff_suggestion?.name || "No staff matched"} icon={UserCheck} />
          <Tile label="Specials" value={
            checklist.special_instructions?.length
              ? `${checklist.special_instructions.length} flag(s)`
              : "None"
          } icon={AlertTriangle} tone={checklist.special_instructions?.length ? "red" : "green"} />
        </div>
        {checklist.special_instructions?.length > 0 && (
          <ul className="mt-2 space-y-0.5">
            {checklist.special_instructions.map((s, i) => (
              <li key={i} className="text-[11px] flex items-start gap-1"
                  style={{ color: s.startsWith("URGENCY") || s.includes("MISSING") ? "#9B1C1C" : "#0B2545" }}
                  data-testid={`ops-special-${i}`}>
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

// =============================================================================
const Section = ({ title, icon: Icon, tone = "navy", children, testid }) => {
  const t = TINT[tone] || TINT.navy;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <span className="grid place-items-center h-6 w-6 rounded-md shrink-0" style={{ background: t.bg, color: t.color }}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <h4 className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>{title}</h4>
      </div>
      {children}
    </div>
  );
};

const Row = ({ label, value, tone = "navy" }) => {
  const t = TINT[tone];
  return (
    <div className="flex items-center justify-between">
      <span style={{ color: "#64748B" }}>{label}</span>
      <span className="font-semibold px-2 py-0.5 rounded" style={{ background: t.bg, color: t.color }}>{value}</span>
    </div>
  );
};

const Tile = ({ label, value, icon: Icon, tone = "navy" }) => {
  const t = TINT[tone];
  return (
    <div className="rounded-lg border p-2.5 bg-white" style={{ borderColor: "#E2E8F0" }}>
      <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "#64748B" }}>
        <Icon className="h-3 w-3 inline mr-1 -mt-0.5" style={{ color: t.color }} />
        {label}
      </div>
      <div className="text-xs font-medium mt-0.5" style={{ color: "#0B2545" }}>{value}</div>
    </div>
  );
};

const Badge = ({ children, tone = "navy" }) => {
  const t = TINT[tone];
  return (
    <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded"
          style={{ background: t.bg, color: t.color }}>
      {children}
    </span>
  );
};

const DispatchAssistantCard = ({ a, onApplyStaff }) => (
  <div className="rounded-lg border p-3 flex items-start gap-2"
       style={{ background: "rgba(15,109,93,0.06)", borderColor: "rgba(15,109,93,0.3)" }}
       data-testid="ops-assistant">
    <span className="grid place-items-center h-7 w-7 rounded-lg shrink-0"
          style={{ background: "rgba(15,109,93,0.15)", color: "#0F6D5D" }}>
      <Clock className="h-3.5 w-3.5" />
    </span>
    <div className="flex-1">
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-0.5" style={{ color: "#0F6D5D" }}>
        Smart Dispatch Assistant · ready to assign
      </div>
      <div className="text-xs" style={{ color: "#0B2545" }}>
        Recommended: <b>{a.recommended_staff?.name || "—"}</b>
        {a.estimated_arrival && <> · est. arrival <b>{a.estimated_arrival.slice(11, 16)}</b></>}
        {a.appointment_window && <> · window {a.appointment_window.start.slice(11, 16)}–{a.appointment_window.end.slice(11, 16)}</>}
        {a.estimated_travel_time_min != null && <> · {a.estimated_travel_time_min} min travel</>}
      </div>
    </div>
    {a.recommended_staff && (
      <Button size="sm" onClick={() => onApplyStaff?.(a.recommended_staff)}
              style={{ background: "#0F6D5D", color: "#FFFFFF" }}
              data-testid="ops-assistant-assign">
        <Send className="h-3.5 w-3.5 mr-1.5" /> Assign now
      </Button>
    )}
  </div>
);
